import { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { apiGetWishlist, apiAddToWishlist, apiRemoveFromWishlist } from '../api';
import logger from '../utils/logger';

const WishlistContext = createContext();

const wishlistReducer = (state, action) => {
  switch (action.type) {
    case 'SET_WISHLIST':
      return { ...state, items: action.payload };
    case 'ADD_TO_WISHLIST': {
      const exists = state.items.find((item) => item.id === action.payload.id);
      if (exists) return state;
      return { ...state, items: [...state.items, action.payload] };
    }
    case 'REMOVE_FROM_WISHLIST':
      return {
        ...state,
        items: state.items.filter((item) => item.id !== action.payload),
      };
    default:
      return state;
  }
};

export const WishlistProvider = ({ children }) => {
  const [state, dispatch] = useReducer(wishlistReducer, { items: [] });

  const fetchWishlist = useCallback(async () => {
    const token = localStorage.getItem('bookscape_token');
    if (!token) return;
    try {
      const data = await apiGetWishlist();
      const items = (data.wishlist || []).map((item) => ({
        id: item.book.id,
        title: item.book.title,
        author: item.book.author,
        image: item.book.image,
        price: item.book.price,
        originalPrice: item.book.originalPrice,
        discount: item.book.discount,
        usdPrice: item.book.usdPrice,
        usdOriginalPrice: item.book.usdOriginalPrice,
        usdDiscount: item.book.usdDiscount,
        rating: item.book.rating,
      }));
      dispatch({ type: 'SET_WISHLIST', payload: items });
    } catch (e) {
      logger.error('Failed to fetch wishlist', e);
    }
  }, []);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const addToWishlist = async (book) => {
    dispatch({ type: 'ADD_TO_WISHLIST', payload: book });
    const token = localStorage.getItem('bookscape_token');
    if (token) {
      try {
        await apiAddToWishlist(book.id);
      } catch (e) {
        logger.error('Failed to add to wishlist API', e);
      }
    }
  };

  const removeFromWishlist = async (id) => {
    dispatch({ type: 'REMOVE_FROM_WISHLIST', payload: id });
    const token = localStorage.getItem('bookscape_token');
    if (token) {
      try {
        await apiRemoveFromWishlist(id);
      } catch (e) {
        logger.error('Failed to remove from wishlist API', e);
      }
    }
  };

  const isInWishlist = (id) => state.items.some((item) => item.id === id);

  return (
    <WishlistContext.Provider
      value={{
        wishlist: state.items,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        wishlistCount: state.items.length,
        fetchWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => useContext(WishlistContext);
